import { useCallback, useEffect, useRef, useState } from 'react';
import { errMsg } from '../lib/api';

/**
 * Ultimele date aduse, după cheia paginii, doar în memoria filei: când revii pe
 * o pagină, o vezi pe loc, iar datele noi vin în fundal și o înlocuiesc. Nu
 * supraviețuiesc unei reîncărcări a paginii și se golesc la intrare, ieșire și
 * încărcarea datelor demo, ca o firmă să nu vadă nici o clipă datele alteia.
 */
const cache = new Map<string, unknown>();
/** Destul pentru paginile din bară și ultimele cereri deschise. */
const CACHE_MAX = 60;

function remember(key: string, value: unknown) {
  cache.delete(key);
  cache.set(key, value);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value!);
}

export function clearAsyncCache() {
  cache.clear();
}

/**
 * Încărcare simplă de date, cu stare de așteptare și de eroare.
 *
 * `loading` e adevărat doar cât timp nu există nimic de arătat. O reîncărcare
 * (după o modificare sau la întoarcerea pe pagină) lasă datele vechi pe ecran
 * până vin cele noi, fără să treacă prin „Se încarcă…”.
 *
 * `key` (opțional) leagă datele de memoria de mai sus. Trebuie să cuprindă tot
 * ce schimbă răspunsul: id-ul, filtrele.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[], key?: string) {
  const cached = key !== undefined && cache.has(key) ? (cache.get(key) as T) : null;
  const [data, setDataState] = useState<T | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(cached === null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  const shown = useRef(data);
  shown.current = data;

  const setData = useCallback(
    (value: T | null | ((prev: T | null) => T | null)) => {
      const next =
        typeof value === 'function' ? (value as (p: T | null) => T | null)(shown.current) : value;
      shown.current = next;
      setDataState(next);
      if (key !== undefined && next !== null) remember(key, next);
    },
    [key],
  );

  // Altă cerere (alt id, alt filtru): datele de dinainte nu mai sunt ale ei.
  const [shownRun, setShownRun] = useState(() => run);
  if (shownRun !== run) {
    setShownRun(() => run);
    setDataState(cached);
    setError(null);
    setLoading(cached === null);
  }

  const reload = useCallback(() => {
    let alive = true;
    // fără nimic pe ecran (de pildă „Încearcă din nou” după o eroare): așteptare vizibilă
    if (shown.current === null) setLoading(true);
    run()
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (alive) setError(errMsg(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [run, setData]);

  useEffect(() => reload(), [reload]);

  return { data, setData, error, loading, reload };
}
